import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../../api/client';
import Logo from '../../../../assets/AptiviewLogo.png';
import ReCaptcha from '../../../common/ReCaptcha';
import CustomToastContainer from '../../../CustomToastContainer';
import AIInterviewTheme from './AIInterviewTheme';
import 'bootstrap-icons/font/bootstrap-icons.css';

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const currentYear = new Date().getFullYear();
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification');
      return;
    }
    
    setIsLoading(true);

    try {
        await api.post('/auth/request-password-reset/', {
        email: email,
        recaptcha_response: recaptchaToken
      });
      
      setIsSuccess(true);
      toast.success('Password reset instructions have been sent to your email address. Please check your inbox and follow the instructions to reset your password.');
    } catch (err) {
      console.error('Password reset error:', err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.email?.[0] ||
        'Failed to send reset instructions. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
      display: 'flex',
      padding: 0,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Custom Toast Container */}
      <CustomToastContainer />
      
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
      }} />

      <div className="row flex-fill w-100 m-0">
        {/* Left Side - AI Interview Theme */}
        <AIInterviewTheme 
          title="Secure Reset"
          description="Reset your password securely. We'll send you instructions to regain access to your account safely."
        />

        {/* Right Side - Reset Password Form */}
        <div className="col-md-4 d-flex align-items-center justify-content-center p-4">
          <div 
            className="w-100 p-5"
            style={{ 
              maxWidth: '450px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {/* Logo and Header */}
            <div className="text-center mb-5">
              <div className="mb-4" style={{ display: 'flex', justifyContent: 'center', }}>
                <img
                  src={Logo}
                  alt="AptiView Logo"
                  style={{ 
                     transform: 'scale(0.9)',
                  }}
                />
              </div>
              <h1 
                style={{ 
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: '800', 
                  fontSize: '2rem',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}
              >
                {isSuccess ? 'Check Your Email' : 'Reset Password'}
              </h1>
              <p style={{ 
                color: '#6b7280', 
                fontSize: '1rem', 
                fontWeight: '500',
                margin: 0
              }}>
                {isSuccess 
                  ? 'We\'ve sent you reset instructions' 
                  : 'Enter your email to receive reset instructions'
                }
              </p>
            </div>

            {!isSuccess && (
              <>
                {/* Reset Password Form */}
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label 
                      htmlFor="reset-email"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.875rem'
                      }}
                    >
                      <i className="bi bi-envelope me-2"></i>
                      Email Address
                    </label>
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        fontSize: '1rem',
                        color: '#111827',
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        transition: 'all 0.15s ease',
                        backdropFilter: 'blur(10px)',
                        fontFamily: 'Inter, sans-serif'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#0ea5e9';
                        e.target.style.boxShadow = '0 0 0 4px rgba(14, 165, 233, 0.1)';
                        e.target.style.background = 'white';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                        e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                      }}
                    />
                  </div>

                  {/* reCAPTCHA */}
                  <div style={{ marginBottom: '1rem' }}>
                    <ReCaptcha
                      siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onVerify={setRecaptchaToken}
                      theme="light"
                      size="normal"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{ 
                      width: '100%',
                      padding: '1rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      fontFamily: 'Poppins, sans-serif',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                      color: 'white',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      marginTop: '1rem',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <i className="bi bi-arrow-clockwise me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                        Sending Instructions...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope-arrow-up me-2"></i>
                        Send Reset Instructions
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {/* Back to Login Link */}
            <div className="text-center mt-4 mb-4">
              <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
                Remember your password?{' '}
                <Link 
                  to="/login" 
                  style={{ 
                    color: '#0ea5e9',
                    textDecoration: 'none',
                    fontWeight: '600',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#0369a1'}
                  onMouseLeave={(e) => e.target.style.color = '#0ea5e9'}
                >
                  Back to Sign In
                </Link>
              </p>
            </div>

            {isSuccess && (
              <div className="text-center mt-4">
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => {
                      setIsSuccess(false);
                      // Clear any existing toasts when trying again
                      toast.dismiss();
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0ea5e9',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    try again
                  </button>
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-4">
              <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: 0 }}>
                 © {currentYear} AptiView. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}